import { STATE_KNOWLEDGE } from "@/components/aposEngine/knowledgeBank";
import {
  buildDailyRecommendation,
  makeCheckInRecord,
} from "@/components/daily/dailyState";
import {
  applyImmediateSupportMode,
  selectBreathingProtocol,
} from "@/components/daily/stateMatchedSupport";

const arrayify = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const objectify = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const normalizeScheduledActivities = (value) =>
  arrayify(value)
    .filter((activity) => activity && typeof activity === "object")
    .map((activity, index) => ({
      ...activity,
      id: activity.id || `scheduled-${index + 1}`,
      days: arrayify(activity.days),
    }));

export function sanitizeAssessmentForCurrentMatch(assessment = {}) {
  const answers = objectify(assessment.answers);
  const schedule = objectify(answers.schedule);
  const profile = objectify(assessment.profile);

  return {
    ...assessment,
    profile: {
      ...profile,
      key: profile.key || "regulation-building",
      title: String(profile.title || "Nervous-system baseline"),
      body: String(
        profile.body ||
          "The assessment provides the longer-term nervous-system baseline for current matching.",
      ),
    },
    answers: {
      ...answers,
      state: objectify(answers.state),
      inputs: objectify(answers.inputs),
      capacity: objectify(answers.capacity),
      current: objectify(answers.current),
      behaviors: arrayify(answers.behaviors),
      schedule: {
        ...schedule,
        preferredActivities: arrayify(schedule.preferredActivities),
        familiarActivities: arrayify(schedule.familiarActivities),
        preserveActivities: arrayify(schedule.preserveActivities),
        resources: arrayify(schedule.resources),
        scheduledActivities: normalizeScheduledActivities(schedule.scheduledActivities),
        availability: objectify(schedule.availability),
        times: objectify(schedule.times),
      },
    },
  };
}

const minutesFor = (checkIn = {}) => {
  const available = Math.max(2, Number(checkIn.minutes) || 5);
  if (available <= 3) return 2;
  if (available <= 7) return 3;
  return 5;
};

const relativeBand = (checkIn = {}) => {
  const intensity = Number(checkIn.intensity) || 3;
  const pressure = Number(checkIn.actionPressure) || 3;
  const energy = Number(checkIn.energy) || 3;
  const choice = Number(checkIn.behavioralChoice) || 3;
  const demand = intensity * 0.35 + pressure * 0.3 + energy * 0.2 + (6 - choice) * 0.15;
  if (demand <= 2.2) return { band: 1, label: "Minimal relative demand" };
  if (demand <= 3) return { band: 2, label: "Low relative demand" };
  if (demand <= 3.8) return { band: 3, label: "Moderate relative demand" };
  return { band: 4, label: "Vigorous relative demand" };
};

const safePreparation = (checkIn = {}, breathing = false) => {
  const steps = [];
  if (["little", "some", "unsure"].includes(checkIn.hydration)) {
    steps.push("Use an appropriate amount of water before beginning.");
  }
  if (["not-recent", "light", "unsure"].includes(checkIn.nourishment)) {
    steps.push("Use familiar nourishment when recent intake does not support the planned input.");
  }
  if (!breathing && Number(checkIn.behavioralChoice) <= 2) {
    steps.push("Use a clear start and stop point and keep the input easy to interrupt.");
  }
  return {
    required: steps.length > 0,
    steps,
    summary: steps.join(" ") || "No additional preparation was required by this check-in.",
  };
};

const safeImmediate = (checkIn = {}, stateMatch) => {
  const mode = checkIn.supportMode || "auto";
  const minutes = minutesFor(checkIn);
  const lowEnergy = Number(checkIn.energy) <= 2;
  const highDemand =
    Number(checkIn.intensity) >= 4 || Number(checkIn.actionPressure) >= 4;
  const exercisePaused = checkIn.physicalReadiness === "pause";

  if (mode === "breathing") {
    const protocol = selectBreathingProtocol({ checkIn, stateMatch });
    return {
      title: protocol.title,
      activityKey: "state-matched-breathing",
      activityType: "breathing",
      outputClass: "somatic",
      fallbackLevel: "somatic",
      durationMinutes: protocol.durationMinutes,
      duration: `${protocol.durationMinutes} minutes · Very light`,
      intensity: "Very light",
      instruction: `Follow the breathing circle continuously. Breathe in for ${protocol.inhaleSeconds} seconds while the circle expands, then breathe out for ${protocol.exhaleSeconds} seconds while it shrinks. Keep the breath comfortable and do not hold it.`,
      breathingProtocol: protocol,
    };
  }

  if (mode === "somatic" || exercisePaused || lowEnergy) {
    return {
      title: highDemand
        ? "Low-intensity somatic grounding"
        : "Gentle somatic movement",
      activityKey: "resilient-somatic-support",
      activityType: "somatic",
      outputClass: "somatic",
      fallbackLevel: "somatic",
      durationMinutes: minutes,
      duration: `${minutes} minutes · Very light`,
      intensity: "Very light",
      instruction:
        "Use slow, comfortable shoulder, trunk, hand, hip, and foot movement. Keep the range easy to stop and notice whether tension, action pressure, attention, or behavioral control changes.",
      breathingProtocol: null,
    };
  }

  return {
    title: highDemand
      ? "Controlled familiar rhythmic movement"
      : "Familiar low-to-moderate movement",
    activityKey: "resilient-familiar-movement",
    activityType: "moderate-output",
    outputClass: "moderate-output",
    fallbackLevel: "moderate",
    durationMinutes: minutes,
    duration: `${minutes} minutes · Light to moderate`,
    intensity: "Light to moderate",
    instruction:
      "Use a familiar movement you can perform without learning under pressure, such as walking, marching, easy cycling, mobility, or a simple bodyweight pattern. Keep control, technique, and the ability to stop available throughout.",
    breathingProtocol: null,
  };
};

export function buildSafeCurrentMatch({ assessment = {}, checkIn = {}, error = null } = {}) {
  const stateKey = checkIn.primaryState || "calm-steady";
  const state = STATE_KNOWLEDGE[stateKey] || STATE_KNOWLEDGE["calm-steady"];
  const label = state.label || stateKey;
  const band = relativeBand(checkIn);
  const stateMatch = {
    modelVersion: "resilient-current-match-v1",
    principle: "Nervous-system state matching + nervous-system training = regulation",
    finalBand: band.band,
    finalBandLabel: band.label,
    explanation:
      "APOS preserved the current nervous-system report and used a conservative, controllable match while bypassing incompatible saved schedule data.",
    recoveryReason: error?.message || null,
  };
  const immediate = safeImmediate(checkIn, stateMatch);
  const preparation = safePreparation(
    checkIn,
    immediate.activityType === "breathing",
  );
  const cooldown = {
    required: immediate.activityType !== "breathing",
    title: "Complete the recovery transition",
    duration: immediate.activityType === "breathing" ? 0 : 2,
    instruction:
      immediate.activityType === "breathing"
        ? "Finish the final exhale comfortably and notice what changed before moving on."
        : "Reduce the pace gradually, then notice breathing, tension, action pressure, attention, and behavioral control.",
  };
  const system = state.system || "Autonomic nervous-system response";
  const target = state.target || "maintain-capacity";
  const participantExplanation =
    state.participantBiology ||
    "Your current report provides the main information for this immediate nervous-system match.";
  const targetExplanation =
    state.targetExplanation ||
    "The immediate aim is to preserve behavioral control and use a manageable physiological input.";

  return {
    profileKey: assessment.profile?.key || "regulation-building",
    recoveredFromMatchError: true,
    stateMatch,
    currentState: {
      labels: [label],
      intensity: Number(checkIn.intensity) || 3,
      energy: Number(checkIn.energy) || 3,
      system,
      target,
      summary: `${label} · intensity ${Number(checkIn.intensity) || 3}/5 · energy ${Number(checkIn.energy) || 3}/5`,
    },
    stateInterpretation: {
      system,
      physiologicalTarget: target,
      participantExplanation,
      clinicianExplanation: state.clinicianBiology || participantExplanation,
      targetExplanation,
    },
    readiness: {
      level: checkIn.physicalReadiness === "pause" ? "pause" : "moderate",
      title: "Current readiness preserved",
      reason:
        "The immediate match remains within the physical-readiness choice reported in this check-in.",
    },
    exerciseEligibility: {
      level: checkIn.physicalReadiness === "pause" ? "pause" : "moderate",
    },
    preparation,
    immediate: {
      ...immediate,
      physiologicalTarget: target,
      rationale: `${participantExplanation} ${targetExplanation}`,
      reassess:
        "Afterward, record whether tension, urgency, energy, attention, or behavioral choice changed.",
    },
    cooldown,
    routines: {
      morning: {
        title: "Morning support",
        instruction: "Use the regular schedule and current check-in to decide whether additional support is needed.",
      },
      bedtime: {
        title: "Bedtime support",
        instruction: "Preserve the existing sleep and recovery routine unless a later check-in indicates a change.",
      },
    },
    week: { recommendations: [] },
  };
}

export function buildResilientCurrentMatch(args = {}) {
  const sanitizedAssessment = sanitizeAssessmentForCurrentMatch(args.assessment);
  try {
    const calculated = buildDailyRecommendation({
      ...args,
      assessment: sanitizedAssessment,
    });
    const plan = applyImmediateSupportMode(calculated, args.checkIn || {});
    if (!plan?.immediate || !plan?.currentState) {
      throw new Error("The current match returned an incomplete plan.");
    }
    return { plan, recovered: false, error: null };
  } catch (error) {
    console.error("APOS deterministic current match used the resilient path", error);
    return {
      plan: buildSafeCurrentMatch({
        assessment: sanitizedAssessment,
        checkIn: args.checkIn || {},
        error,
      }),
      recovered: true,
      error,
    };
  }
}

export function makeResilientCheckInRecord({
  checkIn = {},
  plan = {},
  biometrics = null,
  assessmentCompletedAt = null,
}) {
  try {
    return makeCheckInRecord({
      checkIn,
      plan: {
        ...plan,
        week: {
          ...(plan.week || {}),
          recommendations: plan.week?.recommendations || [],
        },
      },
      biometrics,
      assessmentCompletedAt,
    });
  } catch (error) {
    console.error("APOS check-in record used the resilient path", error);
    const immediate = plan.immediate || {};
    const currentState = plan.currentState || {};
    return {
      id: `checkin-${Date.now()}`,
      createdAt: new Date().toISOString(),
      baselineCompletedAt: assessmentCompletedAt,
      levelBefore: Number(checkIn.level) || 3,
      energyBefore: Number(checkIn.energy) || 3,
      actionPressureBefore: Number(checkIn.actionPressure) || 3,
      behavioralChoiceBefore: Number(checkIn.behavioralChoice) || 3,
      somaticResponseBefore: Number(checkIn.somaticResponse) || 3,
      cognitiveCaptureBefore: Number(checkIn.cognitiveCapture) || 3,
      recoveryDifficultyBefore: Number(checkIn.recoveryDifficulty) || 3,
      reportedStates: arrayify(currentState.labels),
      system: currentState.system || "Nervous-system response",
      physiologicalTarget: currentState.target || "maintain-capacity",
      environment: checkIn.environment || "inside",
      minutesAvailable: Number(checkIn.minutes) || 3,
      physicalReadiness: checkIn.physicalReadiness || "light-moderate",
      hydration: checkIn.hydration || "unsure",
      nourishment: checkIn.nourishment || "unsure",
      profileKey: plan.profileKey || "regulation-building",
      stateMatch: plan.stateMatch || null,
      recommendation: {
        title: immediate.title || "Matched physiological support",
        activityKey: immediate.activityKey || null,
        activityType: immediate.activityType || null,
        duration: immediate.duration || null,
        instruction: immediate.instruction || "Use the matched input and observe the response.",
        fallbackLevel: immediate.fallbackLevel || null,
      },
      preparation: plan.preparation || { required: false, steps: [] },
      cooldown: plan.cooldown || null,
      routines: plan.routines || {},
      alternatives: [],
      biometrics,
      completed: false,
      completedAt: null,
      response: null,
      responseMagnitude: null,
      changeMagnitude: null,
      improvementRating: null,
      adaptivePoints: 0,
      feltWorse: false,
      resilientRecord: true,
    };
  }
}
